// ─────────────────────────────────────────────────────────────────────────────
// WHAT THE BROWSER SDK IS ALLOWED TO SHIP.
//
// 🚨 THIS IS THE LIVE SCRUBBER, AND IT WAS THE ONE NOBODY FIXED. The backend
// Sentry has no DSN — not in render.yaml, not in any .env — so initSentry()
// returns early and the backend has never sent an event. The FRONTEND DSN is
// hardcoded as a fallback in index.js, so the browser SDK is on unconditionally
// and has been since 2026-05-23 (commit af73b3a4).
//
// 🚨 WHAT IT USED TO DO: match /\b(ssn|itin|dob|date_of_birth|password)\b/i
// against `breadcrumbs.values[].data.body` WHEN THAT BODY WAS A STRING, and
// replace the whole body on a hit. So:
//   · no govt_ethnicity, govt_race, govt_sex, govt_info_provided — the
//     §1002.6(b)(9) monitoring information was never named;
//   · no marital_status, no dob_month/day/year as separate keys;
//   · nothing on event.request, event.extra or event.contexts at all;
//   · a breadcrumb whose data was an OBJECT rather than a string was untouched.
//
// 🔑 EXTRACTED FROM index.js SO IT CAN BE TESTED. It was a closure inside
// Sentry.init, which means the only way to exercise it was to initialise Sentry.
// A scrubber nobody can call is a scrubber nobody has checked — the same reason
// the backend one was extracted.
//
// ⚠️ THE KEY LIST MIRRORS backend/utils/applicationFormData MONITORING_KEYS. The
// frontend cannot import from backend/, so these exist twice by necessity and
// backend/test/sentryScrubParity.test.js fails if they diverge. Same arrangement
// as downPaymentBands.js, and for the same reason.
// ─────────────────────────────────────────────────────────────────────────────

// Mirrors backend MONITORING_KEYS plus the identifiers and secrets.
export const MONITORING_KEYS = [
  'govt_ethnicity', 'govt_race', 'govt_sex', 'govt_info_provided',
  'govt_demographics',
  'marital_status',
  'dob_month', 'dob_day', 'dob_year',
  // ⚠️ THE LEGACY DOB SPELLINGS. routes/admin.js reads
  // `fd.dob_year || fd.birth_year` — the same date under a second name, live in
  // stored records — so a scrubber that knows one spelling redacts half the
  // dates of birth that reach Sentry. date_of_birth was already in REDACT_KEYS
  // below and is now here too, where the parity check can see it.
  'birth_month', 'birth_day', 'birth_year', 'date_of_birth',
];

export const REDACT_KEYS = [
  'ssn', 'itin', 'date_of_birth', 'dob', 'password', 'payment_intent_id',
  ...MONITORING_KEYS,
];

const REDACTED = '[REDACTED]';

/**
 * ⚠️ DEPTH-LIMITED AND CYCLE-SAFE. beforeSend runs synchronously on the error
 * path in the browser; a deep or self-referential payload must not turn a crash
 * report into a frozen tab.
 */
function deepRedact(value, depth = 0, seen = new WeakSet()) {
  if (depth > 8 || value === null || typeof value !== 'object') return value;
  if (seen.has(value)) return value;
  seen.add(value);
  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i += 1) value[i] = deepRedact(value[i], depth + 1, seen);
    return value;
  }
  for (const k of Object.keys(value)) {
    // 🔑 PRESENT-AND-REDACTED, NOT TRUTHY-AND-REDACTED. govt_info_provided is a
    // boolean whose interesting value is false, and a truthiness check leaves it.
    if (REDACT_KEYS.includes(k)) { value[k] = REDACTED; continue; }
    value[k] = deepRedact(value[k], depth + 1, seen);
  }
  return value;
}

const BODY_RE = new RegExp(`\\b(${REDACT_KEYS.join('|')})\\b`, 'i');

// ─────────────────────────────────────────────────────────────────────────────
// 🚨 QUERY VALUES ARE REDACTED BY DEFAULT. AN ALLOW-LIST, NOT A DENY-LIST.
//
// THE FIRST VERSION NAMED THE SECRETS: invite, token, reset_token, key, auth.
// That is the wrong shape for the same reason an exclusion list of routes was —
// it protects what somebody thought of. A census of this app's own URLs turned
// up 26 distinct query parameters, and the deny-list covered five of them. It
// missed `t` (message attachments), `q` and `query` (search boxes, which carry
// borrower names and addresses as typed), `conversation`, `destination`,
// `pulled_at`, and `id`. It would miss the next one by construction.
//
// 🔑 SO EVERY VALUE GOES UNLESS ITS NAME IS LISTED. The list is short,
// deliberately, and every entry is a parameter that names a VIEW rather than a
// person or a credential: which tab, which step, which metric, what date range.
// Those are what make a crash report readable; nothing else in a URL is.
//
// ⚠️ utm_* IS KEPT BY PREFIX because campaign parameters are the point of
// having them and there is an unbounded number of them. `gclid` is NOT kept: it
// is a click identifier, and an identifier is the thing this redacts.
//
// 🚨 THE FRAGMENT GOES TOO. #access_token=… is how OAuth implicit flows hand
// out credentials, and a fragment is part of the URL string the SDK captures.
// Nothing in this app routes on a fragment, so there is nothing to lose.
//
// ⚠️ DELIBERATELY NOT ALLOW-LISTED, and each for a reason: `state` (two-letter
// code here, but the name is an OAuth term and the value is low-value in a
// report), `entity` (an accounting entity name), `return` (a return path, which
// carries its own query string inside it), `category` kept — it is an alert
// category, a fixed vocabulary.
const ALLOWED_QUERY_PARAMS = [
  'step', 'tab', 'view', 'scope', 'metric', 'zoom', 'days', 'category',
  'type', 'coborrower', 'addassets', 'start', 'end',
];
const ALLOWED_PREFIXES = ['utm_'];

const paramAllowed = (name) => {
  const n = String(name || '').toLowerCase();
  return ALLOWED_QUERY_PARAMS.includes(n) || ALLOWED_PREFIXES.some((p) => n.startsWith(p));
};

/**
 * Redact every query value whose name is not allow-listed, and the fragment.
 *
 * 🔑 PARSED AS A QUERY STRING, NOT PATTERN-MATCHED. The deny-list version used
 * one regex per secret name; an allow-list has to see every parameter, which
 * means splitting the string rather than searching it.
 *
 * ⚠️ THE PATH AND THE PARAMETER NAMES SURVIVE. "which page, with which
 * parameters set" is most of the value of a URL in a crash report; the values
 * are where the secrets and the identities are.
 *
 * @returns {*} the input with non-allowed values replaced, if it is a string.
 */
export function scrubUrl(value) {
  if (typeof value !== 'string' || !value) return value;
  // 🚨 THE FRAGMENT FIRST, and unconditionally — see the header.
  const hashAt = value.indexOf('#');
  let head = hashAt >= 0 ? value.slice(0, hashAt) : value;
  const frag = hashAt >= 0 ? `#${REDACTED}` : '';

  const qAt = head.indexOf('?');
  if (qAt < 0) return head + frag;
  const path = head.slice(0, qAt);
  const query = head.slice(qAt + 1);
  // ⚠️ SPLIT ON & AND ;, both of which separate parameters in practice, and
  // keep an empty segment rather than dropping it — a URL that comes back
  // shorter than it went in is one somebody has to wonder about.
  const parts = query.split(/([&;])/);
  const out = parts.map((seg, i) => {
    if (i % 2 === 1) return seg;              // the separator itself
    if (!seg) return seg;
    const eq = seg.indexOf('=');
    if (eq < 0) return seg;                   // a bare flag has no value to leak
    const name = seg.slice(0, eq);
    return paramAllowed(name) ? seg : `${name}=${REDACTED}`;
  }).join('');
  return `${path}?${out}${frag}`;
}

/** Sentry beforeSend for the browser SDK. Returns the event, scrubbed in place. */
export function scrubEvent(event) {
  if (!event || typeof event !== 'object') return event;
  if (event.request && typeof event.request === 'object') {
    deepRedact(event.request.data);
    deepRedact(event.request.query_string);
    deepRedact(event.request.headers);
    // 🚨 A URL IS A STRING, SO deepRedact CANNOT SEE INSIDE IT. It redacts by
    // key name; the query lives in one value. Hence scrubUrl, which applies
    // ALLOWED_QUERY_PARAMS to the parameters it parses out.
    event.request.url = scrubUrl(event.request.url);
    // 🚨 query_string COMES AS EITHER A STRING OR AN OBJECT, depending on the
    // SDK and the integration, and the first version only handled the string.
    // An object fell through to deepRedact, which redacts by KEY NAME — so it
    // kept every value whose name was not in REDACT_KEYS, which is now the
    // opposite of the rule.
    const qs = event.request.query_string;
    if (typeof qs === 'string') {
      event.request.query_string = scrubUrl(`?${qs}`).replace(/^\?/, '');
    } else if (qs && typeof qs === 'object') {
      for (const k of Object.keys(qs)) {
        if (!paramAllowed(k)) qs[k] = REDACTED;
      }
    }
  }
  // ⚠️ THE TRANSACTION NAME CAN BE A URL TOO on a pageload transaction.
  if (typeof event.transaction === 'string') event.transaction = scrubUrl(event.transaction);
  deepRedact(event.extra);
  deepRedact(event.contexts);

  // ⚠️ THE BROWSER SDK NESTS BREADCRUMBS UNDER .values, the Node SDK does not.
  // Handling only one shape is how a scrubber passes its own test and ships
  // nothing on the platform it runs on.
  const crumbs = event.breadcrumbs?.values || event.breadcrumbs;
  if (Array.isArray(crumbs)) {
    for (const b of crumbs) {
      if (!b || !b.data) continue;
      // 🚨 A SERIALISED BODY IS OPAQUE, SO THE WHOLE VALUE GOES. Redacting keys
      // inside a JSON string would need parsing it, and a body that fails to
      // parse would then ship intact.
      for (const field of ['body', 'data', 'response_body', 'request_body']) {
        if (typeof b.data[field] === 'string' && BODY_RE.test(b.data[field])) {
          b.data[field] = '[REDACTED — contains PII]';
        }
      }
      // 🚨 NAVIGATION BREADCRUMBS ARE WHERE THE TOKEN ACTUALLY LIVES. A history
      // breadcrumb records from/to for every route change, so the invite URL is
      // in the trail of any error later in the session — long after the page
      // itself removed it from the address bar.
      for (const field of ['from', 'to', 'url']) {
        if (typeof b.data[field] === 'string') b.data[field] = scrubUrl(b.data[field]);
      }
      deepRedact(b.data);
    }
  }
  return event;
}
